import { useState } from 'react'
import { Link } from 'react-router-dom'
import { apiService } from '@/services/api'
import { FREE_SEAT_TAKEN_SENTENCE, TEAM_GATE_CODES, teamRefusal } from '@/utils/teamSeats'
import { Button, ButtonLink, Dialog, Input, Notice } from '@/design-system/primitives'
import {
  INVITE_BIGGER_PLAN,
  INVITE_CLOSE,
  INVITE_COPY_FAILED,
  INVITE_COPY_LINK,
  INVITE_FAILED,
  INVITE_LINK_LABEL,
  INVITE_LINK_READY,
  INVITE_NEEDS_PLAN,
  INVITE_SEAT_FULL,
  INVITE_SEE_INVITATIONS,
  INVITE_SEE_PLANS,
  INVITE_TITLE,
  inviteLead,
} from './firstWeekCopy'

/*
  A second door to the team invite. Same endpoint, same seat check, same
  join page. The seat-limit error the service throws becomes a sentence.
  Plan 3 adds the email field and "Send invite"; this version copies a link.

  The lead says what the person invited will do, and that differs by guide:
  the free guide has no pixel step, so its invite promises none.
*/

/*
  Why the server would not make an invite. It says which with a code
  (TEAM_GATE_CODES in server/src/services/teamService.ts), and teamRefusal
  reads it; the words are read only when a body carries no code.

  TEAM_SEAT_LIMIT       a team on a plan with every seat taken: `full`.
  TEAM_PLAN_REQUIRED    a team with no plan, where the one invite is not on
                        offer: `needs_plan`. Trying again never gets past it,
                        so the dialog must not say "try again": it says
                        inviting comes with a plan, and offers them.
  FREE_TEAM_SEAT_TAKEN  a team with no plan whose one seat is taken or
                        already offered: `seat_taken`. The server's own
                        sentence is shown as it came, because the same code
                        arrives with other words from a join.
*/

/* Where a full team on a plan goes next, so the seat notice is not a dead end. A team with no plan cannot open it. */
const TEAM_INVITATIONS_PATH = '/team/settings/invitations'
const BILLING_PLANS_PATH = '/settings/billing'
/* The plans themselves, for a team that has none. */
const CHOOSE_PLAN_PATH = '/settings/billing?section=plans#subscription-plans'

type InviteState = 'idle' | 'working' | 'ready' | 'copy_failed' | 'full' | 'needs_plan' | 'seat_taken' | 'failed'

export function FirstWeekInviteDialog({
  open,
  onClose,
  onChanged,
  planName,
  variant = 'trial',
}: {
  open: boolean
  onClose: () => void
  onChanged: () => void
  planName: string | null
  /** Which guide the invite was opened from. */
  variant?: 'trial' | 'free'
}) {
  const [state, setState] = useState<InviteState>('idle')
  /*
    The created link is kept so a clipboard that refuses does not cost a
    second invite: pressing the button again copies the one we already made.
  */
  const [link, setLink] = useState<string | null>(null)
  /* What the server said when the one seat of a team with no plan was taken or offered. */
  const [seatTakenSentence, setSeatTakenSentence] = useState<string>(FREE_SEAT_TAKEN_SENTENCE)

  async function copyLink() {
    setState('working')

    let url = link
    if (!url) {
      try {
        const res = await apiService.createTeamInvite('member')
        const token = res.data?.inviteToken
        if (!token) throw new Error('no token')
        url = `${window.location.origin}/team/join/${token}`
        setLink(url)
      } catch (error: unknown) {
        const refusal = teamRefusal(error)
        if (refusal.code === TEAM_GATE_CODES.FREE_SEAT_TAKEN) {
          setSeatTakenSentence(refusal.sentence ?? FREE_SEAT_TAKEN_SENTENCE)
          setState('seat_taken')
        } else {
          setState(refusal.code === TEAM_GATE_CODES.SEAT_LIMIT ? 'full' : refusal.code === TEAM_GATE_CODES.PLAN_REQUIRED ? 'needs_plan' : 'failed')
        }
        return
      }
    }

    /*
      The invite exists on the server from here on, whatever the clipboard
      does, so the guide is told to refetch before the copy is attempted.
    */
    onChanged()

    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard')
      await navigator.clipboard.writeText(url)
      setState('ready')
    } catch {
      setState('copy_failed')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={INVITE_TITLE}
      lead={inviteLead(variant)}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {INVITE_CLOSE}
          </Button>
          <Button variant="primary" onClick={copyLink} loading={state === 'working'}>
            {INVITE_COPY_LINK}
          </Button>
        </>
      }
    >
      {state === 'ready' && <Notice kind="good">{INVITE_LINK_READY}</Notice>}
      {state === 'copy_failed' && link && (
        <>
          <Notice kind="look">{INVITE_COPY_FAILED}</Notice>
          <Input readOnly value={link} aria-label={INVITE_LINK_LABEL} onFocus={(e) => e.currentTarget.select()} />
        </>
      )}
      {state === 'full' && (
        <>
          <Notice kind="look">{INVITE_SEAT_FULL(planName)}</Notice>
          <ButtonLink variant="secondary" to={TEAM_INVITATIONS_PATH}>
            {INVITE_SEE_INVITATIONS}
          </ButtonLink>{' '}
          <Link to={BILLING_PLANS_PATH}>{INVITE_BIGGER_PLAN}</Link>
        </>
      )}
      {state === 'needs_plan' && (
        <>
          <Notice kind="look">{INVITE_NEEDS_PLAN}</Notice>
          <ButtonLink variant="secondary" to={CHOOSE_PLAN_PATH}>
            {INVITE_SEE_PLANS}
          </ButtonLink>
        </>
      )}
      {state === 'seat_taken' && (
        /*
          The plans, and nothing else. Only a workspace with no plan is
          refused this way, and for it the invitations page is the paid-plan
          gate: a link there would send the person to a page that turns
          them away, under a sentence that says their one seat is in use.
        */
        <>
          <Notice kind="look">{seatTakenSentence}</Notice>
          <Link to={CHOOSE_PLAN_PATH}>{INVITE_SEE_PLANS}</Link>
        </>
      )}
      {state === 'failed' && <Notice kind="look">{INVITE_FAILED}</Notice>}
    </Dialog>
  )
}
